function JumpNav() {
  const links: [string, string][] = [
    ["overview", "Overview"],
    ["requester", "For requesters"],
    ["timebot", "Using /timebot in Slack"],
    ["approver", "For managers"],
    ["executive", "For executives"],
    ["integrations", "How the integrations work"],
    ["help-contact", "Getting help"],
  ];
  return (
    <nav className="help-jumpnav">
      {links.map(([id, label]) => (
        <a key={id} href={`#${id}`} className="help-jumplink">
          {label}
        </a>
      ))}
    </nav>
  );
}

export default function Help() {
  return (
    <div className="page help-page">
      <div className="page-header">
        <h1 className="di-h3">User guide</h1>
      </div>

      <JumpNav />

      <section id="overview" className="di-card">
        <h2 className="di-h4">What this app is for</h2>
        <p className="di-p">
          Dispatch Time Off handles time-off requests and approvals for contractors and global staff
          who fall outside Rise, Dispatch's main HR system. If you're covered by Rise, you'll be
          redirected there instead -- this app isn't for you, and your vacation/sick/personal time
          stays booked and tracked in Rise.
        </p>
        <p className="di-p">
          Everyone else who's active in the <b>Team Roster spreadsheet</b> gets a version of this app
          shaped by their role -- which requests they can submit, whose requests they approve, and
          whether they see company-wide reporting. It's normal to hold more than one of these at once
          (e.g. someone can both submit their own requests and approve their team's).
        </p>
        <p className="di-p">
          Two different relationships share this app, and it's worth knowing which one is yours:{" "}
          <b>global team members</b> are genuine employees who fall outside Rise (usually because
          Rise doesn't support their country) -- for you, this app <i>is</i> your real time-off
          approval process, exactly as it would be if you were on Rise. <b>Contractors</b> set their
          own schedule and aren't subject to approval in that sense -- submitting a request here is
          about coordination and visibility, not asking permission for something that's already yours
          to decide. Either way the workflow and buttons look identical (the mechanics are the same),
          but what an acknowledgement actually represents differs depending on which one you are.
        </p>
        <p className="di-p">
          Everything you do here quietly keeps other places in sync, so nobody has to update them by
          hand: your manager gets notified in <b>Slack</b>, acknowledged time off blocks your
          availability in <b>Forecast</b>, and it shows up as an event on both the shared{" "}
          <b>Team Time Off</b> Google Calendar and <b>your own Google Calendar</b>. More on exactly
          how in <a href="#integrations">How the integrations work</a> below.
        </p>
        <p className="di-p">
          There's also a <b>Team calendar</b> tab, visible to everyone regardless of role -- a
          month view of who's out and when, across the whole team.
        </p>
      </section>

      <section id="requester" className="di-card">
        <h2 className="di-h4">For requesters</h2>
        <p className="di-p">
          This is you if you're a contractor or global staff member who submits your own time off.
          You'll land on <b>My requests</b> when you sign in.
        </p>

        <h3 className="help-subhead">Submitting a request</h3>
        <ol className="help-steps">
          <li>Click <b>New request</b>.</li>
          <li>
            Choose a type: <b>Vacation</b>, <b>Sick Time</b>, or <b>Other Time Off</b>. Sick Time is
            logged automatically with no approval needed -- it's there so your absence is visible and
            synced everywhere, not to ask permission to be sick.
          </li>
          <li>Pick your start and end date. For a single day, you can also mark it as a half day.</li>
          <li>Add an optional note for your approver, then submit.</li>
        </ol>
        <p className="di-p">
          If you're doing client-facing work, the form also has a reminder to coordinate your
          schedule with your Project Managers -- this app handles the internal record, not the
          client-side heads-up.
        </p>
        <p className="di-p">
          Vacation and Other Time Off go to your manager as <b>pending</b> until they decide. You'll
          see the status update on this page once they do -- your manager's name is shown in the{" "}
          <b>Manager</b> column, so you know who to check in with if it's taking a while. Once
          something's acknowledged, it also shows up automatically as an event on your own Google
          Calendar -- no need to add it yourself.
        </p>
        <p className="di-p">
          The app won't let you submit a request that overlaps with one you already have pending or
          acknowledged for the same time -- you'll get a message naming the conflicting request instead.
          The one exception is half days: a morning request and an afternoon request on the same day
          can coexist, since together they don't overlap.
        </p>

        <h3 className="help-subhead">Cancelling</h3>
        <p className="di-p">
          You can cancel a request that's still pending, or one that's already been acknowledged
          (plans change). Cancelling an acknowledged request also removes it from the shared calendar
          and Forecast automatically.
        </p>
        <p className="di-p">
          You can also do all of this from Slack without opening the app at all -- see{" "}
          <a href="#timebot">Using /timebot in Slack</a> below.
        </p>
      </section>

      <section id="timebot" className="di-card">
        <h2 className="di-h4">Using /timebot in Slack</h2>
        <p className="di-p">
          Slack's <code className="help-code">/timebot</code> slash command handles the everyday stuff
          without opening the app at all -- submitting, checking, and cancelling requests right from
          your channel or DM with the bot.
        </p>
        <ul className="help-steps">
          <li>
            <code className="help-code">/timebot</code> -- opens a short form to submit a new request
            (type, dates, optional half day, note) directly in Slack.
          </li>
          <li>
            <code className="help-code">/timebot upcoming</code> -- lists your own upcoming pending and
            acknowledged requests.
          </li>
          <li>
            <code className="help-code">/timebot cancel</code> -- the same list, with a Cancel button
            (and a confirmation, since it can't be undone from there) on each one.
          </li>
          <li>
            <code className="help-code">/timebot team</code> -- shows who else is out in the next two
            weeks, company-wide.
          </li>
          <li>
            <code className="help-code">/timebot open</code> -- posts a link to open Dispatch Time Off
            in your browser, for anything the other commands don't cover.
          </li>
          <li>
            <code className="help-code">/timebot help</code> -- shows this same list of commands, right
            in Slack.
          </li>
        </ul>
        <p className="di-p">
          Everything submitted or cancelled through <code className="help-code">/timebot</code> goes
          through the exact same rules as the app -- the same eligibility checks, the same conflict
          detection, and the same Slack/Calendar/Forecast sync -- so a request made in Slack shows up on{" "}
          <b>My requests</b> here exactly like one made in the app, and vice versa.
        </p>
      </section>

      <section id="approver" className="di-card">
        <h2 className="di-h4">For managers</h2>
        <p className="di-p">
          This is you if anyone's <b>manager</b> field in the Team Roster spreadsheet points to you.
          You'll see an <b>Acknowledgements</b> tab, showing requests from your direct reports only --
          not the whole company.
        </p>

        <h3 className="help-subhead">Deciding on a request</h3>
        <ol className="help-steps">
          <li>
            <b>Awaiting your decision</b> lists anything pending from your team. <b>Past decisions</b>{" "}
            shows what you've already acted on, including a <b>Decided</b> column with exactly when.
          </li>
          <li>
            <b>Acknowledge</b> confirms it immediately -- it'll sync to the calendar and Forecast
            right away.
          </li>
          <li>
            <b>Needs Coordination</b> asks for a short note first, which gets sent back to the
            requester so they know why.
          </li>
        </ol>
        <p className="di-p">
          You can do the exact same thing from <b>Slack</b> -- every request posts to the shared
          time-off channel with Acknowledge/Needs Coordination buttons, and acting there or in the app
          has the same effect either way. Normally only you (the actual manager on file) can act on a
          given request; anyone else who clicks those buttons gets a private note explaining why
          nothing happened, and the request stays untouched for you to handle. The one exception is if
          you're currently away yourself -- see Backup acknowledgements below.
        </p>
        <p className="di-p">
          Sick Time never reaches you -- it's auto-acknowledged on submission, since it's a notice,
          not a request.
        </p>
        <p className="di-p">
          What "deciding" means depends on who's asking, per <a href="#overview">the note above</a>:
          for a global team member, you're genuinely approving their time off. For a contractor,
          there's no approval to give -- Acknowledge just confirms you've seen it and it's on your
          radar for scheduling, since the time off itself isn't yours to grant or withhold.
        </p>
      </section>

      <section id="executive" className="di-card">
        <h2 className="di-h4">For executives</h2>
        <p className="di-p">
          This is a small set of people with company-wide visibility, granted manually rather than
          derived from the spreadsheet (there's no "executive" column to read it from). You'll see an{" "}
          <b>Org-wide</b> tab: acknowledged time off broken down by type, by month, and by person,
          plus the full request list across everyone -- not just your own reports.
        </p>

        <h3 className="help-subhead">Filtering by year</h3>
        <p className="di-p">
          A year picker at the top scopes everything on the page -- the stats, the month-by-month
          breakdown, the by-person chart, and the request list -- to that calendar year. It defaults
          to the current year and only lists years that actually have data (plus the current one,
          even before anything's happened in it yet).
        </p>

        <h3 className="help-subhead">Drilling into a type</h3>
        <p className="di-p">
          Tap a <b>Vacation</b>, <b>Sick Time</b>, or <b>Other Time Off</b> stat card to expand the
          list of exactly which acknowledged requests make up that number, for the selected year. Tap
          it again to collapse it.
        </p>

        <h3 className="help-subhead">By person</h3>
        <p className="di-p">
          A stacked bar per person shows their acknowledged time off for the selected year, in days
          (weekends don't count, half-days count as half a day), split by type using the same colors
          as the stat cards above. Bars are scaled relative to whoever has taken the most, so it's
          easy to spot who's taken a lot of time off and who hasn't taken much at all.
        </p>

        <h3 className="help-subhead">Exporting to CSV</h3>
        <p className="di-p">
          <b>Download CSV</b> exports every request for the selected year -- any status, not just
          acknowledged -- with dates, type, status, notes, and decision details included, for whatever
          further analysis you want to do outside the app.
        </p>

        <h3 className="help-subhead">All requests</h3>
        <p className="di-p">
          The full request list at the bottom includes two extra columns not shown elsewhere:{" "}
          <b>Submitted</b> (when the request was originally made) and <b>Acknowledged by</b> (who
          actually decided it). For Sick Time, which is auto-acknowledged and never has an explicit
          decider, Acknowledged by falls back to showing the person's manager, since that's who it
          would have gone to if it needed a decision.
        </p>

        <p className="di-p">
          Executives also see an <b>Admin</b> tab, covering the two things that keep the system
          running day to day rather than reporting on it -- kept separate from Org-wide so that
          reporting tab doesn't get cluttered.
        </p>

        <h3 className="help-subhead">Executive access</h3>
        <p className="di-p">
          The Admin tab's <b>Executive access</b> panel lists every active person with a one-click
          grant/revoke button. That's the only way this gets managed now -- if someone new needs the
          Org-wide view, an existing executive grants it there. (There always has to be at least one
          executive, so the app won't let you remove the last one.)
        </p>

        <h3 className="help-subhead">Backup acknowledgements</h3>
        <p className="di-p">
          The Admin tab's <b>Backup acknowledgements</b> section is normally empty -- it only lists a
          pending request when that person's own manager is currently away (has their own acknowledged
          time off covering today, or has been manually flagged away -- see below). Any active executive
          can step in and Acknowledge or mark it Needs Coordination from there, the same as the primary
          approver could. This is a shared pool, not a fixed 1:1 backup assignment -- whichever executive
          gets to it first handles it.
        </p>
        <p className="di-p">
          You'll also get an active Slack nudge (with an @-mention) in the shared time-off channel when
          this happens -- either right away if the manager was already away when the request came in,
          or shortly after if the manager goes away while it's still pending. Acting on it in Slack (or
          in the app) works exactly like a normal decision.
        </p>
        <p className="di-p">
          Sometimes a manager is genuinely out but there's no acknowledged request to detect it from --
          out sick without logging it, travelling, etc. For that, the Admin tab's{" "}
          <b>Manual away override</b> section lets any executive flag a manager as away through a chosen
          date. That opens backup coverage exactly the same as a real request would, and clears itself
          once the date passes (or can be cleared early).
        </p>
      </section>

      <section id="integrations" className="di-card">
        <h2 className="di-h4">How the integrations work</h2>

        <h3 className="help-subhead">Team Roster spreadsheet</h3>
        <p className="di-p">
          This is where the app gets its facts about people: who's active, who's on Rise (and
          therefore not using this app at all), and who reports to whom. It's synced into the app
          every 30 minutes -- the app never reads it live. If your eligibility, manager, or name looks
          wrong here, it needs fixing in that spreadsheet, not in the app itself; the fix shows up
          within half an hour.
        </p>

        <h3 className="help-subhead">Slack</h3>
        <p className="di-p">
          Every request posts to a shared channel: Vacation/Other Time Off requests post with
          Acknowledge/Needs Coordination buttons for the manager, and Sick Time posts as an FYI with
          no buttons since it's already decided. Whatever happens next -- acknowledged, sent back for
          coordination, cancelled -- updates that same message so the channel always reflects the
          current status, rather than piling up duplicate posts.
        </p>
        <p className="di-p">
          You can also skip the app entirely and use Slack's <code className="help-code">/timebot</code>{" "}
          command to submit, check, and cancel requests -- see{" "}
          <a href="#timebot">Using /timebot in Slack</a> above.
        </p>

        <h3 className="help-subhead">Forecast</h3>
        <p className="di-p">
          Once a request is acknowledged, it appears in Forecast as a time-off allocation under the
          matching category (Vacation / Sick Time / Other Time Off), so your availability there stays
          accurate without anyone updating it by hand. If you don't have a Forecast account, this step
          is skipped quietly -- it's treated as normal, not an error, and doesn't affect anything else.
          Cancelling an acknowledged request removes the Forecast allocation the same way it was added.
        </p>

        <h3 className="help-subhead">Team Time Off calendar</h3>
        <p className="di-p">
          Acknowledging a request also creates an all-day event on the shared Team Time Off Google
          Calendar, so anyone with access to it can see who's out at a glance -- outside of this app
          entirely. Cancelling removes that event.
        </p>
        <p className="di-p">
          <a
            className="di-btn di-btn-outline di-btn-small"
            href="https://calendar.google.com/calendar/u/0/r?cid=c_27136338e38a0665f33594284e4bc65887abd5010c410fefd689eeccff40af04%40group.calendar.google.com"
            target="_blank"
            rel="noreferrer"
          >
            Open Team Time Off calendar
          </a>
        </p>

        <h3 className="help-subhead">Your own calendar</h3>
        <p className="di-p">
          Acknowledging a request also creates the same all-day event directly on your own Google
          Calendar, marked as busy so it actually blocks your availability for scheduling -- unlike
          the shared calendar above, which is just a visibility view and doesn't affect anyone's
          free/busy status. Cancelling removes this event too.
        </p>

        <p className="di-small">
          If any of these ever fails to sync (an outage, a bad match, etc.), it's retried
          automatically for a while rather than silently staying broken -- and it never blocks or
          reverses the decision itself, which is always the source of truth.
        </p>
      </section>

      <section id="help-contact" className="di-card">
        <h2 className="di-h4">Getting help</h2>
        <p className="di-p">
          If something here isn't working the way this guide describes, or you're just stuck, reach
          out to <b>Cam</b> directly -- either message him in Slack, or email{" "}
          <a href="mailto:cameron@dispatchintegration.com">cameron@dispatchintegration.com</a>.
        </p>
      </section>
    </div>
  );
}
